import Icon from "components/ui/Icon";
import React from "react";
import styled from "styled-components";

const Check = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1;

  display: none;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;

  pointer-events: none;

  svg {
    * {
      stroke-width: 10px;
      stroke: ${(props) => props.theme.colors.primary};
    }
  }
`;

const Label = styled.div`
  position: relative;

  flex: 1 1 auto;

  font-weight: ${(props) => props.theme.fontWeights.normal};
  font-size: ${(props) => props.theme.fontSizes.sm}px;
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  display: none;
  width: 100%;
  height: 100%;

  background: ${(props) => props.theme.colors.secondary};
  border-radius: ${(props) => props.theme.borderRadius.small}px;
`;

const StyledRadio = styled.input`
  position: relative;
  z-index: 1;

  display: block;
  height: 24px;
  width: 24px;
  margin: 0;
  padding: 0;
  flex: 0 0 auto;

  background: white;
  border: 1px solid ${(props) => props.theme.colors.text}40;
  border-radius: 9999px;
  outline: 0;
  appearance: none;

  font-family: ${(props) => props.theme.fonts.body};
  font-size: ${(props) => props.theme.fontSizes.sm}px;

  &:checked {
    border-color: ${(props) => props.theme.colors.secondary};

    & ~ ${Check} {
      display: flex;
    }

    & ~ ${Background} {
      display: block;

      border-color: ${(props) => props.theme.colors.primary};
    }

    & ~ ${Label} {
      color: ${(props) => props.theme.reverseColors.secondary};
    }
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const Wrapper = styled.label`
  position: relative;

  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 16px;
  gap: 12px;

  border-radius: ${(props) => props.theme.borderRadius.small}px;
  box-shadow: 0 0 0 1px
    color-mix(in srgb, ${(props) => props.theme.colors.text} 10%, transparent)
    inset;
`;

export interface RadioProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  value: any;
  label: React.ReactNode;
}

const Radio = (
  { label, ...props }: RadioProps,
  ref: React.Ref<HTMLInputElement>
) => (
  <Wrapper>
    <StyledRadio {...props} type="radio" ref={ref} />
    <Check>
      <Icon name="checkmarkSolid" color="primary" size="xs" />
    </Check>
    <Background />
    <Label>{label}</Label>
  </Wrapper>
);

export default React.forwardRef(Radio);
